import React, { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db, registerFcmToken } from "../firebase";
import { notificationsGranted } from "../nativePush";
import DuprConnectButton, { DuprConnectState } from "./DuprConnectButton";

/**
 * Tervetulovaihe ensimmäisen kirjautumisen jälkeen (docs/ideat.md E).
 *
 * Kutsulinkki saa pelaajan sisään ovesta ja siihen se loppui: ilmoituslupa
 * ja DUPR-yhdistys olivat kumpikin nappeja profiilin pohjalla, eikä mikään
 * ohjannut niihin. Pelaaja saattoi olla klubissa viikkoja ilman että sai
 * yhtäkään ilmoitusta tai että yksikään ottelu meni DUPR:iin — eikä hän
 * eikä järjestäjä huomannut mitään.
 *
 * Tämä näytetään kerran. `onboardedAt` kirjoitetaan myös ohitettaessa,
 * jotta tämä ei jää vaivaamaan joka avauksella; molemmat napit jäävät
 * profiiliin niille jotka palaavat asiaan myöhemmin.
 */
export default function Welcome({
  clubId, playerId, playerName, clubName, duprState, lang, onDone, onOpenProfile,
}: {
  clubId: string;
  playerId: string;
  playerName: string;
  clubName?: string;
  duprState?: DuprConnectState;
  lang: "fi" | "en";
  onDone: () => void;
  onOpenProfile: () => void;
}) {
  const fi = lang === "fi";
  const [notif, setNotif] = useState<"idle" | "loading" | "ok" | "fail">("idle");
  const [dupr, setDupr] = useState<DuprConnectState | undefined>(duprState);

  useEffect(() => {
    notificationsGranted().then(g => { if (g) setNotif("ok"); });
  }, []);

  async function finish() {
    try {
      await updateDoc(doc(db, "clubs", clubId, "players", playerId), {
        onboardedAt: new Date().toISOString(),
      });
    } catch { /* merkintä ei saa estää sisään pääsyä */ }
    onDone();
  }

  async function allowNotifications() {
    setNotif("loading");
    const token = await registerFcmToken("player", clubId, "player", playerName);
    setNotif(token ? "ok" : "fail");
  }

  const C = {
    card: "var(--sl-card)", text: "var(--sl-text)", muted: "var(--sl-muted)",
    border: "var(--sl-border)", primary: "var(--sl-primary)",
    soft: "var(--sl-soft)", green: "#22c55e",
  };
  const card: React.CSSProperties = {
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: 16, padding: 18, marginBottom: 12,
  };
  const btn: React.CSSProperties = {
    width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
    cursor: "pointer", fontWeight: 700, fontSize: 14,
  };
  const title: React.CSSProperties = {
    fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6,
  };
  const desc: React.CSSProperties = {
    fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 12,
  };
  const done: React.CSSProperties = {
    fontSize: 13, fontWeight: 700, color: C.green,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10001, background: "var(--sl-bg)",
      overflowY: "auto", fontFamily: "'Inter', sans-serif",
      paddingTop: "env(safe-area-inset-top, 0px)",
      paddingBottom: "calc(24px + var(--sl-safe-bottom))",
    }}>
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "24px 16px 0" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 6 }}>
          {fi ? `Tervetuloa${clubName ? ` — ${clubName}` : ""}!` : `Welcome${clubName ? ` to ${clubName}` : ""}!`}
        </div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 20 }}>
          {fi
            ? "Kaksi asiaa kannattaa tehdä heti. Kumpikin vie puoli minuuttia, ja molemmat voi tehdä myöhemminkin profiilista."
            : "Two things are worth doing now. Each takes half a minute, and both can be done later from your profile."}
        </div>

        {/* 1. Ilmoitukset */}
        <div style={card}>
          <div style={title}>🔔 {fi ? "Salli ilmoitukset" : "Allow notifications"}</div>
          <div style={desc}>
            {fi
              ? "Ilman tätä kutsut ja muistutukset eivät tavoita sinua — tapahtuma voi mennä ohi ilman että huomaat."
              : "Without this, invitations and reminders will not reach you — an event can pass you by unnoticed."}
          </div>
          {notif === "ok" ? (
            <div style={done}>✓ {fi ? "Ilmoitukset päällä" : "Notifications on"}</div>
          ) : (
            <>
              <button onClick={allowNotifications} disabled={notif === "loading"} style={{
                ...btn, background: C.primary, color: "#fff",
                opacity: notif === "loading" ? 0.6 : 1,
              }}>
                {notif === "loading"
                  ? (fi ? "Rekisteröidään…" : "Registering…")
                  : (fi ? "Salli ilmoitukset" : "Allow notifications")}
              </button>
              {notif === "fail" && (
                <div style={{ ...desc, marginTop: 10, marginBottom: 0 }}>
                  {fi
                    ? "Ei onnistunut. Voit sallia ilmoitukset myöhemmin profiilista tai puhelimen asetuksista."
                    : "That did not work. You can allow notifications later from your profile or phone settings."}
                </div>
              )}
            </>
          )}
        </div>

        {/* 2. DUPR */}
        <div style={card}>
          <div style={title}>🏅 {fi ? "Yhdistä DUPR-tili" : "Connect your DUPR account"}</div>
          <div style={desc}>
            {fi
              ? "Vasta yhdistetty tili saa ottelusi DUPR:iin ja virallisen luvun näkyviin. Pelkkä tunnuksen kirjoittaminen ei riitä."
              : "Only a connected account gets your matches into DUPR and shows your official rating. Typing the ID alone is not enough."}
          </div>
          <DuprConnectButton
            playerId={playerId}
            clubId={clubId}
            playerName={playerName}
            current={dupr}
            onConnected={s => setDupr(s ?? undefined)}
            lang={lang}
          />
        </div>

        {/* 3. Omat tiedot */}
        <div style={card}>
          <div style={title}>👤 {fi ? "Tarkista tietosi" : "Check your details"}</div>
          <div style={desc}>
            {fi
              ? "Nimi ja sähköpostiosoite kannattaa katsoa kerran läpi — kutsut menevät siihen osoitteeseen."
              : "Worth checking your name and email once — invitations go to that address."}
          </div>
          <button onClick={async () => { await finish(); onOpenProfile(); }} style={{
            ...btn, background: C.soft, color: C.text,
          }}>
            {fi ? "Avaa profiili" : "Open profile"}
          </button>
        </div>

        <button onClick={finish} style={{
          ...btn, background: "transparent", color: C.muted,
          fontSize: 13, marginTop: 4,
        }}>
          {fi ? "Valmis" : "Done"}
        </button>
      </div>
    </div>
  );
}
